/**
 * PetHistoryPage — dedicated per-pet care history page.
 *
 * Mirrors the human `HistoryPage` both in structure and in tier
 * behaviour: free tier gets the last 14 days, paid tiers can request
 * up to 365. The backend enforces the cap server-side; we mirror it
 * here for the UX copy and the locked "Plus unlocks full history" card.
 *
 * Data model per day entry:
 *   { date: "YYYY-MM-DD", entries: [ { kind, at, by, by_label }, … ] }
 * where `kind` is one of "walk" | "fed" | "water" (flea/tick treatments
 * live on the pet doc itself — single-stamp fields surfaced in the
 * top banner, not in the per-day list).
 *
 * Design note: we deliberately render a single chronological list
 * rather than tabs by type. A family reading "Sofus this month" wants
 * a truthful rhythm — 3 walks Monday, only 1 Tuesday, fed twice
 * Thursday — not a tab you have to click to find the pattern.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Footprints, UtensilsCrossed, Droplets } from "lucide-react";
import { api } from "@/lib/api";
import { fmtTime } from "@/lib/time";

const DAY_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "1 year" },
];

const KIND_META = {
  walk: { label: "Walked", Icon: Footprints, tint: "text-[#5A4A2E]" },
  fed: { label: "Fed", Icon: UtensilsCrossed, tint: "text-[#7A2E3A]" },
  water: { label: "Fresh water", Icon: Droplets, tint: "text-[#2E5A7A]" },
};

function fmtDate(ymd) {
  try {
    const d = new Date(`${ymd}T00:00:00Z`);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
  } catch {
    return ymd;
  }
}

// Treatments span long intervals (vet wellness is yearly, flea/tick
// monthly-ish) so the year matters for disambiguation. The per-day
// history list keeps the compact format above — everything there is
// within the viewer's selected window so the year adds noise.
function fmtTreatmentDate(ymd) {
  try {
    const d = new Date(`${ymd}T00:00:00Z`);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return ymd;
  }
}

// Time formatting now comes from `lib/time.js` — honours the family's
// "Time format" setting (Auto / 24h / 12h).

function displayActor(entry, members) {
  if (entry.by) {
    const m = (members || []).find((x) => x.id === entry.by);
    if (m) return m.name;
  }
  if (entry.by_label) return entry.by_label;
  return "—";
}

// Small banner at the top showing the two treatment stamps (vet
// wellness + flea/tick). Rendered only when at least one stamp is
// present — a brand-new pet with nothing logged keeps the page calm.
function TreatmentsBanner({ treatments }) {
  const vet = treatments?.vet_wellness_last;
  const vetNext = treatments?.vet_wellness_next;
  const flea = treatments?.flea_tick_last;
  const fleaInterval = treatments?.flea_tick_interval_days;
  if (!vet && !flea) return null;
  return (
    <div
      className="ff-card p-4 md:p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
      data-testid="pet-history-treatments"
    >
      {vet && (
        <div>
          <div className="label-tiny mb-1">Vet wellness</div>
          <div className="font-display text-2xl leading-tight">
            {fmtTreatmentDate(vet)}
          </div>
          {vetNext && (
            <div className="label-tiny text-[color:var(--ink-3)] mt-0.5">
              Next due {fmtTreatmentDate(vetNext)}
            </div>
          )}
        </div>
      )}
      {flea && (
        <div>
          <div className="label-tiny mb-1">Flea &amp; tick</div>
          <div className="font-display text-2xl leading-tight">
            {fmtTreatmentDate(flea)}
          </div>
          {fleaInterval && (
            <div className="label-tiny text-[color:var(--ink-3)] mt-0.5">
              Every {fleaInterval} days
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PetHistoryPage({ members }) {
  const { petId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!petId) return undefined;
    setError(null);
    api
      .petHistory(petId, days)
      .then((res) => {
        if (!alive) return;
        setData(res);
      })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 403) {
          setError("locked");
        } else if (status === 404) {
          setError("not-found");
        } else {
          setError("fetch");
        }
        setData({ days: [], treatments: {} });
      });
    return () => {
      alive = false;
    };
  }, [petId, days]);

  // Local lookup from props when the API response doesn't include
  // a hydrated pet object (e.g. 403 / 404 edge cases).
  const petFromProps = useMemo(
    () => (members || []).find((m) => m.id === petId) || null,
    [members, petId],
  );
  const pet = data?.pet || petFromProps;

  const isPaid = data?.tier && data.tier !== "free";
  const lockedWindow = !isPaid && days > 14;

  if (error === "not-found") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <div className="font-display-italic text-3xl">Pet not found</div>
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="ff-btn"
            data-testid="pet-history-back-404"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error === "locked") {
    return (
      <div className="min-h-screen bg-[color:var(--bg)]">
        <main className="max-w-2xl mx-auto px-5 md:px-6 py-6 md:py-10">
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="ff-chip inline-flex items-center gap-2 mb-8"
            data-testid="pet-history-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div
            className="ff-card p-6 text-center"
            data-testid="pet-history-locked"
          >
            <Lock className="w-6 h-6 mx-auto mb-3 text-[color:var(--ink-3)]" />
            <div className="font-display-italic text-2xl mb-2">
              Beyond your plan's care log
            </div>
            <p className="text-[color:var(--ink-2)] text-sm">
              Plus covers up to 2 pets, Grand covers up to 3, Extended covers every dog and cat in the
              family. Upgrade to unlock this pet's care log — including the
              full walk, feed, water and treatment history.
            </p>
            <button
              type="button"
              onClick={() => {
                try {
                  sessionStorage.setItem("ff_open_billing", "pet-history");
                } catch { /* ignore */ }
                navigate("/app");
              }}
              className="ff-btn mt-4"
              data-testid="pet-history-upgrade"
            >
              See plans →
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--bg)]">
      <main className="max-w-2xl mx-auto px-5 md:px-6 py-6 md:py-10">
        <div className="flex items-center justify-between gap-3 mb-8">
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="ff-chip inline-flex items-center gap-2"
            data-testid="pet-history-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ff-input text-sm"
            data-testid="pet-history-range"
          >
            {DAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-8 flex items-baseline gap-3">
          <span className="text-5xl leading-none">
            {pet?.avatar_emoji || "🐾"}
          </span>
          <div>
            <div className="label-tiny mb-1">
              {pet?.name || "Pet"}'s care log
            </div>
            <h1 className="font-display-italic text-4xl md:text-5xl leading-[0.95] tracking-tight">
              How's the rhythm been?
            </h1>
          </div>
        </div>

        <TreatmentsBanner treatments={data?.treatments || {}} />

        {data === null ? (
          <div className="font-display-italic text-xl text-[color:var(--ink-2)]">
            Loading…
          </div>
        ) : (data.days || []).length === 0 ? (
          <div
            className="ff-card p-6 text-center"
            data-testid="pet-history-empty"
          >
            <div className="font-display-italic text-2xl mb-2">
              Nothing logged yet in this window
            </div>
            <p className="text-[color:var(--ink-2)] text-sm">
              Once walks, feeds and water changes start being logged,
              they'll appear here — newest first.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="pet-history-list">
            {data.days.map((day) => (
              <article
                key={day.date}
                className="ff-card p-4 md:p-5"
                data-testid={`pet-history-day-${day.date}`}
              >
                <div className="label-tiny mb-3">{fmtDate(day.date)}</div>
                <ul className="space-y-2">
                  {day.entries.map((e, i) => {
                    const meta = KIND_META[e.kind] || {
                      label: e.kind,
                      Icon: Footprints,
                      tint: "",
                    };
                    const Icon = meta.Icon;
                    return (
                      <li
                        key={`${e.kind}-${e.at}-${i}`}
                        className="flex items-baseline gap-3"
                        data-testid={`pet-history-entry-${e.kind}`}
                      >
                        <Icon
                          className={`w-4 h-4 shrink-0 translate-y-0.5 ${meta.tint}`}
                          strokeWidth={2}
                        />
                        <span className="font-mono tabular text-sm text-[color:var(--ink-3)] w-12 shrink-0">
                          {fmtTime(e.at)}
                        </span>
                        <span className="flex-1 text-[15px]">
                          <span className="text-[color:var(--ink)] font-medium">
                            {meta.label}
                          </span>{" "}
                          <span className="text-[color:var(--ink-2)]">
                            by {displayActor(e, members)}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </article>
            ))}
          </div>
        )}

        {lockedWindow && (
          <div
            className="mt-6 ff-card p-5 flex items-start gap-3"
            data-testid="pet-history-plus-gate"
          >
            <Lock
              className="w-4 h-4 mt-0.5 text-[color:var(--ink-3)] shrink-0"
              strokeWidth={2}
            />
            <div className="flex-1">
              <div className="label-tiny mb-1">
                Full history · Plus and up
              </div>
              <p className="text-[13px] text-[color:var(--ink-2)] leading-snug">
                Free plans show the last 14 days of care history. Upgrade to
                Plus to scroll back through last season — or print the full
                year for a vet visit.
              </p>
              <button
                type="button"
                onClick={() => {
                  try {
                    sessionStorage.setItem("ff_open_billing", "pet-history");
                  } catch { /* ignore */ }
                  navigate("/app");
                }}
                className="ff-btn mt-3"
                data-testid="pet-history-upgrade-btn"
              >
                Upgrade to Plus →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
